import io from 'socket.io-client';
import { getDiscadorApiUrl } from '../config/runtime-api';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.currentRoom = null;
    this.listeners = new Map();
  }

  /**
   * Conecta ao servidor WebSocket
   */
  connect(roomName) {
    if (roomName) {
      this.currentRoom = roomName;
    }

    if (this.socket?.connected) {
      if (this.currentRoom) {
        this.joinRoom(this.currentRoom);
      }
      return;
    }

    if (this.socket) {
      return;
    }

    const wsUrl = process.env.VUE_APP_WS_URL || getDiscadorApiUrl();

    this.socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.socket.on('connect', (socket) => {
      if (this.currentRoom) {
        this.joinRoom(this.currentRoom);
      }
    });

    this.socket.on('disconnect', () => {});

    this.socket.on('connect_error', (error) => {
      console.error('[Websocket] Falha na conexão:', error.message);
    });
  }

  /**
   * Desconecta do servidor WebSocket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.currentRoom = null;
      this.listeners.clear();
    }
  }

  /**
   * Entra em uma sala
   * @param {string} roomName - Nome da sala
   */
  joinRoom(roomName) {
    if (!roomName) return;

    if (!this.socket?.connected) {
      this.currentRoom = roomName;
      return;
    }

    if (this.currentRoom && this.currentRoom !== roomName) {
      this.socket.emit('leave', this.currentRoom);
    }

    this.socket.emit('join', roomName);
    this.currentRoom = roomName;
  }

  /**
   * Sai de uma sala
   * @param {string} message - Nome da sala
   */
  leaveRoom(message) {
    if (!this.socket?.connected || !message) return;

    this.socket.emit('leave', message);
    if (this.currentRoom === message) {
      this.currentRoom = null;
    }
  }

  /**
   * Escuta eventos de novas mensagens
   * @param {Function} callback - Função a ser chamada quando uma nova mensagem chegar
   * @returns {Function} Função para remover o listener
   */
  onNewMessage(callback) {
    if (!this.socket) {
      return () => {};
    }

    this.removeListeners('newMessage');

    const wrappedCallback = (message) => {
      callback(message);
    };

    this.socket.on('newMessage', wrappedCallback);
    this.listeners.set('newMessage', wrappedCallback);

    return () => {
      this.removeListeners('newMessage');
    };
  }

  /**
   * Escuta eventos de novas mensagens
   * @param {Function} callback - Função a ser chamada quando uma nova mensagem chegar
   * @returns {Function} Função para remover o listener
   */
  onLeftCall(callback) {
    if (!this.socket) {
      return () => {};
    }
    
    this.removeListeners('left');

    const wrappedCallback = (message) => {
      callback(message);
    };

    this.socket.on('left', wrappedCallback);
    this.listeners.set('left', wrappedCallback);

    return () => {
      this.removeListeners('left');
    };
  }

  /**
   * Remove todos os listeners de um evento
   * @param {string} eventName - Nome do evento
   */
  removeListeners(eventName) {
    if (this.socket && this.listeners.has(eventName)) {
      this.socket.off(eventName, this.listeners.get(eventName));
      this.listeners.delete(eventName);
    }
  }

  /**
   * Verifica se está conectado
   * @returns {boolean}
   */
  isConnected() {
    return this.socket?.connected || false;
  }
}

export default new WebSocketService();
